/**
 * LogoutMenu — autenticazione Phase 6 (IMPL-P6-005).
 *
 * UC-009 logout actions surfaced from the {@link AppLayout} navbar. Two
 * actions:
 *  1. "Esci"                          → current device only
 *     calls `useAuthSession().signOut()` (which hits Better Auth `/sign-out`)
 *  2. "Esci da tutti i dispositivi"   → all sessions for the user
 *     calls `authClient.logoutAll()` (custom route)
 *
 * Both actions issue a hard navigate (`window.location.href`) to `/accedi`
 * with a `?logout=ok` query string so the SPA tears down React state and
 * re-hydrates `useAuthSession` from a clean fetch — avoids stale provider
 * state after sign-out.
 *
 * Rendering: shadcn `DropdownMenu` is NOT installed in Phase 6's footprint,
 * so we render a small inline menu (button + collapsible action list) using
 * vanilla button primitives. The trigger is keyboard-accessible (button +
 * aria-expanded) and the menu closes on outside click + Escape.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authClient } from '../lib/auth-client';
import { useAuthSession } from '../hooks/useAuthSession';

export function LogoutMenu() {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<'one' | 'all' | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { signOut } = useAuthSession();

  // Close on outside click + Escape so the menu behaves like a real popover.
  useEffect(() => {
    if (!open) return;

    function handlePointer(event: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  async function handleLogoutOne() {
    if (pending) return;
    setPending('one');
    try {
      await signOut();
    } catch {
      /* signOut already swallows errors — fall through to hard navigate */
    } finally {
      // Hard navigate to clear React state + cookie state.
      window.location.href = '/accedi?logout=ok';
    }
  }

  async function handleLogoutAll() {
    if (pending) return;
    setPending('all');
    try {
      await authClient.logoutAll();
    } catch {
      /* swallow — best effort */
    } finally {
      window.location.href = '/accedi?logout=ok';
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
        <span>Esci</span>
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </Button>

      {open ? (
        <div
          role="menu"
          aria-label="Opzioni di uscita"
          className="absolute right-0 z-40 mt-2 w-64 rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleLogoutOne}
            disabled={pending !== null}
            className="block w-full px-4 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            {pending === 'one' ? 'Uscita in corso…' : 'Esci'}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleLogoutAll}
            disabled={pending !== null}
            className="block w-full px-4 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            {pending === 'all' ? 'Uscita in corso…' : 'Esci da tutti i dispositivi'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
